import React, { useEffect } from "react";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import ButtonWithLoading from "../../../components/common/ButtonWithLoading";
import { Typography } from "@progress/kendo-react-common";
import ShadowCard from "../../../components/common/ShadowCard";
import {
  clearCreditnotevoucherRegisterReportDetails,
  clearCreditnotevoucherRegisterReportList,
  setCreditnotevoucherRegisterReport,
} from "../reportsSlice";
import AlertBox from "../../../components/common/AlertBox";
import { LoadingPanel } from "../../../components/layout/Loading";
import { Button } from "@progress/kendo-react-buttons";
import { checkAcessRights, formatIndianNumber } from "../../../_helper/helper";
import {
  EDIT_ACCESS,
  EXCEL_EXPORT_ACCESS,
  PRINT_ACCESS,
} from "../../../_contstants/common";
import { getAllCreditnotevoucherRegisterReport } from "../services/reports.services";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { BsThreeDotsVertical } from "react-icons/bs";
import { RxCross2 } from "react-icons/rx";
import FormCheckboxGroup from "../../../components/formFields/FormCheckboxGroup";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import moment from "moment";
import FormDatePicker from "../../../components/formFields/FormDateField";
import { useNavigate } from "react-router-dom";

interface FormChangeWatcherProps {
  formRenderProps: FormRenderProps;
}
const ChangeWatcher = ({ formRenderProps }: FormChangeWatcherProps) => {
  useEffect(() => {
    formRenderProps.onChange("abcd", { value: "forenable" });
  }, []);

  return null;
};

const CreditnotevoucherRegisterReportForm: React.FC = () => {
  const gridRef = React.useRef<any>(null);
  const dispatch = useAppDispatch();
  const loading = useAppSelector((state) => state.reports.loading);

  const CreditnotevoucherRegisterReportDetails = useAppSelector(
    (state) => state.reports.CreditnotevoucherRegisterReportDetails
  );
  const CreditnotevoucherRegisterReportList = useAppSelector(
    (state) => state.reports.CreditnotevoucherRegisterReportList
  );
  const isEditRef = React.useRef(false);
  useEffect(() => {
    return () => {
      if (!isEditRef.current) {
        dispatch(clearCreditnotevoucherRegisterReportDetails());
        dispatch(clearCreditnotevoucherRegisterReportList());
      }
      //   dispatch(setCurrentPage(0));
    };
  }, []);

  //   const currentPage = useAppSelector((state) => state.pagination.currentPage);
  //   const pageLimit = useAppSelector((state) => state.pagination.pageLimit);

  const handleSubmit = (values: any) => {
    const payload = {
      from_date: values?.from_date
        ? moment(values?.from_date).format("YYYY-MM-DD")
        : "",
      to_date: values?.to_date
        ? moment(values?.to_date).format("YYYY-MM-DD")
        : "",
    };
    dispatch(getAllCreditnotevoucherRegisterReport(payload));
    dispatch(
      setCreditnotevoucherRegisterReport({
        from_date: values?.from_date,
        to_date: values?.to_date,
      })
    );
  };

  return (
    <GridLayout gap={{ rows: 10 }} cols={[{ width: "100%" }]}>
      <GridLayoutItem>
        <ShadowCard>
          <Form
            initialValues={CreditnotevoucherRegisterReportDetails}
            onSubmit={handleSubmit}
            render={(formRenderProps: FormRenderProps) => (
              <FormElement style={{ width: "100%" }}>
                <GridLayout
                  style={{ marginRight: 30 }}
                  gap={{ rows: 10, cols: 10 }}
                  cols={[
                    { width: "25%" },
                    { width: "25%" },
                    { width: "25%" },
                    { width: "25%" },
                  ]}
                >
                  <ChangeWatcher formRenderProps={formRenderProps} />
                  <GridLayoutItem colSpan={4}>
                    <Typography.h4
                      style={{ marginBottom: 0, marginLeft: "3px" }}
                    >
                      Credit Note Register Report
                    </Typography.h4>
                  </GridLayoutItem>

                  <GridLayoutItem>
                    <Field
                      wrapperStyle={{ width: "100%" }}
                      name="from_date"
                      label="From Date"
                      format={"dd/MM/yyyy"}
                      component={FormDatePicker}
                    />
                  </GridLayoutItem>

                  <GridLayoutItem>
                    <Field
                      wrapperStyle={{ width: "100%" }}
                      name="to_date"
                      label="To Date"
                      format={"dd/MM/yyyy"}
                      minDate={
                        formRenderProps.valueGetter("from_date") ||
                        new Date(1900, 1, 1)
                      }
                      component={FormDatePicker}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <div style={{ marginTop: "35px"}}>
                      <ButtonWithLoading
                        label={"View"}
                        type="submit"
                        disabled={!formRenderProps.allowSubmit || loading}
                        loading={loading}
                      />
                    </div>
                  </GridLayoutItem>
                </GridLayout>
              </FormElement>
            )}
          />
        </ShadowCard>
        {loading ? (
          <LoadingPanel gridRef={gridRef} />
        ) : (
          CreditnotevoucherRegisterReportList &&
          CreditnotevoucherRegisterReportList.length > 0 && (
            <ShadowCard style={{ marginTop: 10, minHeight: "30vh" }}>
              <CreditnotevoucherRegisterExportTable isEditRef={isEditRef} />
            </ShadowCard>
          )
        )}
      </GridLayoutItem>
    </GridLayout>
  );
};

const CreditnotevoucherRegisterExportTable: React.FC<{
  isEditRef: React.MutableRefObject<boolean>;
}> = ({ isEditRef }) => {
  const isExcelAccess = checkAcessRights(
    location.pathname,
    EXCEL_EXPORT_ACCESS
  );
  const navigate = useNavigate();
  const CreditnotevoucherRegisterReportList = useAppSelector(
    (state) => state.reports.CreditnotevoucherRegisterReportList
  );
  const CreditnotevoucherRegisterReportDetails = useAppSelector(
    (state) => state.reports.CreditnotevoucherRegisterReportDetails
  );

  const isPrintAccess = checkAcessRights(location.pathname, PRINT_ACCESS);
  const isEditAccess = checkAcessRights(location.pathname, EDIT_ACCESS);
  const handlePrint = (props: any, type: string) => {
    const state = {
      from_date: props?.from_date
        ? moment(props?.from_date).format("YYYY-MM-DD")
        : "",
      to_date: props?.to_date
        ? moment(props?.to_date).format("YYYY-MM-DD")
        : "",
      initialValues: props.initialValues,
    };

    if (type === "creditnotevoucherregisterreport") {
      sessionStorage.setItem(
        "creditnotevoucherregisterreportState",
        JSON.stringify(state)
      );
      const reportTab = window.open(
        "/creditnotevoucherregisterreportprint",
        "_blank"
      );
      const interval = setInterval(() => {
        if (reportTab?.closed) {
          sessionStorage.removeItem("creditnotevoucherregisterreportState");
          clearInterval(interval);
        }
      }, 1000);
    }
  };

  const handleExportExcel = () => {
    const table = document.querySelector(
      ".employee-details-report-table"
    ) as HTMLTableElement;
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.table_to_sheet(table);

    // Adjust column widths dynamically
    const colWidths: number[] = [];
    const rows = table.querySelectorAll("tr");

    rows.forEach((row) => {
      const cells = row.querySelectorAll("th, td");
      cells.forEach((cell, colIndex) => {
        const cellContent = cell.textContent || "";
        const cellLength = cellContent.length;

        // Calculate width (max of existing or current cell)
        colWidths[colIndex] = Math.max(
          colWidths[colIndex] || 10,
          cellLength + 2
        );
      });
    });

    // Apply column widths
    ws["!cols"] = colWidths.map((width) => ({ wch: width }));
    const headerRows = table.querySelectorAll("thead tr");
    headerRows.forEach((row) => {
      const cells = row.querySelectorAll("th");
      cells.forEach((cell, colIndex) => {
        const cellRef = XLSX.utils.encode_cell({ r: 0, c: colIndex }); // Assume <thead> is row 0
        if (ws[cellRef]) {
          ws[cellRef].s = {
            font: { bold: true }, // Bold font
            alignment: { horizontal: "center", vertical: "center" }, // Center alignment
            fill: { fgColor: { rgb: "D9E1F2" } }, // Light background color
            border: {
              top: { style: "thin", color: { rgb: "000000" } },
              bottom: { style: "thin", color: { rgb: "000000" } },
              left: { style: "thin", color: { rgb: "000000" } },
              right: { style: "thin", color: { rgb: "000000" } },
            },
          };
        }
      });
    });

    Object.keys(ws).forEach((key) => {
      if (!key.startsWith("!")) {
        const cell = ws[key];
        if (cell.z !== "m/d/yy") {
          if (typeof cell.v === "number") {
            // Check if the cell is a date (custom logic depending on your use case)
            const isDate = cell.t === "d"; // 't' might indicate the cell type in some libraries
            if (!isDate) {
              cell.t = "s"; // Set cell type to 'string'
              cell.v = `${cell.v}`; // Convert number to string
            }
          }
        } else if (cell.z === "m/d/yy") {
          cell.z = "DD-MMM-YYYY";
        }
      }
    });

    XLSX.utils.book_append_sheet(wb, ws, `Sheet1`);

    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const data = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(data, `Credit Note Register Report`);
  };
  const [showfilterColumns, setShowFilterColumns] = React.useState(false);
  const initialValues = {
    filter_columns: [
      "financial_year",
      "credit_note_voucher_date",
      "credit_note_voucher_total_amount",
      "ledger_name_entry",
      "voucher_type",
    ],
  };
  const columns = [
    { label: "Financial Year", value: "financial_year" },
    { label: "Date", value: "credit_note_voucher_date" },
    { label: "Total Amount", value: "credit_note_voucher_total_amount" },
    { label: "Ledger Name", value: "ledger_name_entry" },
    { label: "Voucher Type", value: "voucher_type" },
  ];

  const checkInclude = (key: string, formRenderProps: FormRenderProps) => {
    return formRenderProps.valueGetter("filter_columns")?.includes(key);
  };
  return (
    <>
      {CreditnotevoucherRegisterReportList &&
      CreditnotevoucherRegisterReportList.length ? (
        <Form
          initialValues={initialValues}
          render={(formRenderProps: FormRenderProps) => {
            return (
              <FormElement style={{ width: "100%" }}>
                <GridLayout
                  //   style={{ marginRight: 30 }}
                  gap={{ rows: 10, cols: 10 }}
                  cols={[{ width: "100%" }]}
                >
                  <GridLayoutItem className="d-flex justify-content-between align-items-center">
                    <Typography.h4 className="m-0">
                      Credit Note Register Report
                      <>
                        <p style={{ fontSize: 16 ,  margin: 0  }}>
                          For Period :-
                          {CreditnotevoucherRegisterReportDetails.from_date &&
                            CreditnotevoucherRegisterReportDetails.to_date &&
                            `( ${moment(
                              CreditnotevoucherRegisterReportDetails.from_date
                            ).format("DD/MM/YYYY")} - ${moment(
                              CreditnotevoucherRegisterReportDetails.to_date
                            ).format("DD/MM/YYYY")} )`}
                        </p>
                      </>
                    </Typography.h4>
                    <div className="position-relative d-flex">
                      {isPrintAccess && (
                        <Button
                          style={{ marginRight: 5 }}
                          type="button"
                          fillMode={"solid"}
                          themeColor={"primary"}
                          onClick={() =>
                            handlePrint(
                              {
                                ...CreditnotevoucherRegisterReportDetails,
                                initialValues: {
                                  filter_columns:
                                    formRenderProps.valueGetter(
                                      "filter_columns"
                                    ),
                                },
                              },
                              "creditnotevoucherregisterreport"
                            )
                          }
                        >
                          Print
                        </Button>
                      )}
                      {isExcelAccess && (
                        <Button
                          type="button"
                          fillMode={"solid"}
                          themeColor={"primary"}
                          onClick={handleExportExcel}
                          style={{ marginRight: 10 }}
                        >
                          Excel Export
                        </Button>
                      )}
                      <Tooltip
                        anchorElement="target"
                        position="left"
                        parentTitle={true}
                      >
                        <Button
                          title="Filter Columns"
                          type="button"
                          themeColor={"primary"}
                          fillMode={"solid"}
                          onClick={() => setShowFilterColumns(true)}
                        >
                          Filter
                          <BsThreeDotsVertical />
                        </Button>
                      </Tooltip>
                      {showfilterColumns && (
                        <div
                          style={{
                            position: "absolute",
                            top: "32px",
                            right: "0",
                            background: "white",
                            padding: "12px",
                            border: "1px solid whitesmoke",
                            width: "250px",
                            zIndex: 1,
                          }}
                        >
                          <div className="d-flex justify-content-between align-items-baseline">
                            <p className="m-0 fw-600">Filter</p>
                            <Button
                              themeColor={"primary"}
                              fillMode={"flat"}
                              onClick={() => setShowFilterColumns(false)}
                            >
                              <RxCross2 />
                            </Button>
                          </div>
                          <hr className="m-1" style={{ color: "lightgray" }} />
                          <Field
                            wrapperClassName="d-flex flex-column checkBoxGroup-vertical"
                            inputClassName="checkBoxGroup-vertical-input"
                            id={"filter_columns"}
                            name={"filter_columns"}
                            marginRight={0}
                            marginBeetween={8}
                            component={FormCheckboxGroup}
                            options={columns}
                          />
                        </div>
                      )}
                    </div>
                  </GridLayoutItem>
                  <GridLayoutItem
                    style={{
                      maxHeight: "calc(100vh - 340px)",
                      overflow: "scroll",
                      scrollbarWidth: "thin",
                    }}
                  >
                    <table
                      cellSpacing={1}
                      cellPadding={2}
                      className="table table-bordered w-100  employee-details-report-table"
                      style={{
                        marginTop: 1,
                      }}
                    >
                      <thead>
                        <tr
                          className="text-center fw-600"
                          style={{
                            fontSize: 12,
                            position: "sticky",
                            top: 0,
                          }}
                        >
                          <th style={{ minWidth: 150 }}>Credit Note No</th>

                          {checkInclude("voucher_type", formRenderProps) && (
                            <>
                              <th style={{ minWidth: 150 }}>Voucher Type</th>
                            </>
                          )}
                          {checkInclude(
                            "credit_note_voucher_date",
                            formRenderProps
                          ) && (
                            <>
                              <th style={{ minWidth: 100 }}>Date</th>
                            </>
                          )}

                          {checkInclude("financial_year", formRenderProps) && (
                            <>
                              <th style={{ minWidth: 100 }}>Financial Year</th>
                            </>
                          )}
                          {checkInclude(
                            "ledger_name_entry",
                            formRenderProps
                          ) && (
                            <>
                              <th style={{ minWidth: 200 }}>Account Name</th>
                            </>
                          )}

                          {checkInclude(
                            "credit_note_voucher_total_amount",
                            formRenderProps
                          ) && (
                            <>
                              <th style={{ minWidth: 150 }}>Total Amount</th>
                            </>
                          )}
                        </tr>
                      </thead>
                      {/* Table Body */}
                      <tbody>
                        {CreditnotevoucherRegisterReportList.map(
                          (item: any, rowIndex: number) => (
                            <>
                              <tr key={rowIndex}>
                                <td>
                                  {
                                    <div
                                      className={
                                        isEditAccess
                                          ? "hoverableName"
                                          : "normalName"
                                      }
                                      onClick={() => {
                                        isEditAccess &&
                                        item?.common_voucher_type ==
                                          "SALESRETURN"
                                          ? navigate("/creditnote/edit", {
                                              state: {
                                                credit_note_guid:
                                                  item?.voucher_guid,
                                              },
                                            })
                                          : navigate(
                                              "/creditnotevoucher/edit",
                                              {
                                                state: {
                                                  credit_note_voucher_guid:
                                                    item?.voucher_guid,
                                                },
                                              }
                                            );
                                        isEditRef.current = true;
                                      }}
                                    >
                                      {item?.credit_note_voucher_no_string}
                                    </div>
                                  }
                                </td>

                                {checkInclude(
                                  "voucher_type",
                                  formRenderProps
                                ) && <td>{item?.voucher_type}</td>}
                                {checkInclude(
                                  "credit_note_voucher_date",
                                  formRenderProps
                                ) && (
                                  <td>
                                    {moment(
                                      item?.credit_note_voucher_date,
                                      "DD/MM/YYYY"
                                    ).format("DD-MMM-YYYY")}
                                  </td>
                                )}
                                {checkInclude(
                                  "financial_year",
                                  formRenderProps
                                ) && <td>{item?.financial_year}</td>}
                                {checkInclude(
                                  "ledger_name_entry",
                                  formRenderProps
                                ) && <td>{item?.ledger_name_entry}</td>}

                                {checkInclude(
                                  "credit_note_voucher_total_amount",
                                  formRenderProps
                                ) && (
                                  <td className="text-end">
                                    {formatIndianNumber(
                                      item?.credit_note_voucher_total_amount ||
                                        0
                                    )}
                                  </td>
                                )}
                              </tr>
                            </>
                          )
                        )}
                        
                      </tbody>
                    </table>
                  </GridLayoutItem>
                </GridLayout>
              </FormElement>
            );
          }}
        />
      ) : (
        <AlertBox />
      )}
    </>
  );
};

export default CreditnotevoucherRegisterReportForm;
